import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { Badge, Card, CardContent, CardHeader, CardTitle, TableScroll } from '@mlm/ui';
import { getCourse, getCourseEnrollments, getCourseStats } from '../lib/api';
import { ActivitySparkline } from '../components/stats';

/** Per-course stats: lesson funnel (where learners drop off), 30-day series, roster. */
export function CourseStatsPage() {
  const { id = '' } = useParams();
  const { data: course } = useQuery({
    queryKey: ['creator-course', id],
    queryFn: () => getCourse(id),
  });
  const { data: stats } = useQuery({
    queryKey: ['creator-course-stats', id],
    queryFn: () => getCourseStats(id),
  });
  const { data: roster = [] } = useQuery({
    queryKey: ['creator-course-enrollments', id],
    queryFn: () => getCourseEnrollments(id),
  });

  return (
    <div className="max-w-5xl space-y-4">
      <div>
        <Link to={`/courses/${id}`} className="text-xs text-muted-foreground hover:underline">
          ← {course?.productName ?? 'Course'}
        </Link>
        <h1 className="text-2xl font-semibold">Statistics</h1>
      </div>

      {stats && stats.tests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Test results</CardTitle>
            <p className="text-sm text-muted-foreground">
              Attempts, passes and pending reviews per test.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <TableScroll>
              <table className="w-full text-sm">
                <thead className="border-b text-left text-muted-foreground">
                  <tr>
                    <th className="p-3">Test</th>
                    <th className="p-3 text-right">Attempts</th>
                    <th className="p-3 text-right">Passed</th>
                    <th className="p-3 text-right">Pending review</th>
                    <th className="p-3 text-right">Avg score</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.tests.map((t) => (
                    <tr key={t.testId} className="border-b last:border-0">
                      <td className="p-3">
                        {t.chapterTitle && (
                          <span className="text-xs text-muted-foreground">{t.chapterTitle} · </span>
                        )}
                        {t.title}
                        {t.isPractice && (
                          <Badge variant="outline" className="ml-2">
                            Practice
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-right tabular-nums">{t.attempts}</td>
                      <td className="p-3 text-right tabular-nums">{t.passed}</td>
                      <td className="p-3 text-right tabular-nums">{t.pending}</td>
                      <td className="p-3 text-right tabular-nums">
                        {t.avgScorePct != null ? `${t.avgScorePct}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Lesson funnel</CardTitle>
          <p className="text-sm text-muted-foreground">
            How many learners opened and finished each lesson — the drop-off shows where to improve.
          </p>
        </CardHeader>
        <CardContent className="p-0">
          {!stats || stats.funnel.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No lessons yet.</p>
          ) : (
            <TableScroll>
              <table className="w-full text-sm">
                <thead className="border-b text-left text-muted-foreground">
                  <tr>
                    <th className="p-3">Lesson</th>
                    <th className="p-3 text-right">Opened</th>
                    <th className="p-3 text-right">Completed</th>
                    <th className="p-3 text-right">Completion</th>
                    <th className="p-3 text-right">Total plays</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.funnel.map((r) => (
                    <tr key={r.lessonId} className="border-b last:border-0">
                      <td className="p-3">
                        <span className="text-xs text-muted-foreground">{r.chapterTitle} · </span>
                        {r.title}
                      </td>
                      <td className="p-3 text-right tabular-nums">{r.opened}</td>
                      <td className="p-3 text-right tabular-nums">{r.completed}</td>
                      <td className="p-3 text-right tabular-nums">
                        <div className="flex items-center justify-end gap-2">
                          <div className="h-1.5 w-24 rounded bg-muted">
                            <div
                              className="h-1.5 rounded bg-primary"
                              style={{ width: `${r.completionPct}%` }}
                            />
                          </div>
                          {r.completionPct}%
                        </div>
                      </td>
                      <td className="p-3 text-right tabular-nums">{r.opens}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Last 30 days</CardTitle>
        </CardHeader>
        <CardContent>
          <ActivitySparkline points={stats?.series ?? []} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Learners</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {roster.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No learners yet.</p>
          ) : (
            <TableScroll>
              <table className="w-full text-sm">
                <thead className="border-b text-left text-muted-foreground">
                  <tr>
                    <th className="p-3">Learner</th>
                    <th className="p-3">How</th>
                    <th className="p-3">Since</th>
                    <th className="p-3">Progress</th>
                    <th className="p-3">Last activity</th>
                    <th className="p-3">Tests</th>
                    <th className="p-3">Certificate</th>
                    <th className="p-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {roster.map((r, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="p-3">{r.userName ?? 'Learner'}</td>
                      <td className="p-3 capitalize">{r.source.replace('_', ' ')}</td>
                      <td className="p-3 text-muted-foreground">
                        {new Date(r.grantedAt).toLocaleDateString()}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-16 overflow-hidden rounded bg-muted">
                            <div
                              className="h-full bg-primary"
                              style={{ width: `${Math.min(100, r.progressPct)}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {r.lessonsCompleted}/{r.lessonCount}
                          </span>
                        </div>
                      </td>
                      <td className="p-3 text-muted-foreground">
                        {r.lastActivityAt ? new Date(r.lastActivityAt).toLocaleDateString() : '—'}
                      </td>
                      <td className="p-3 text-muted-foreground">
                        {r.testsRequired > 0 ? `${r.testsPassed}/${r.testsRequired}` : '—'}
                      </td>
                      <td className="p-3 text-muted-foreground">
                        {r.certificateIssuedAt
                          ? new Date(r.certificateIssuedAt).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className="p-3">{r.revokedAt ? 'Refunded' : 'Active'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
